<template>
  <div class="service-multiselect">
    <el-input
      v-model="search"
      placeholder="Найти услугу"
      @focus="handleFocus"
      @input="handleSearchInput"
      @keydown.down.prevent="highlightNext"
      @keydown.up.prevent="highlightPrev"
      @keydown.enter.prevent="enterSelect"
      @keydown.esc.prevent="closeDropdown"
      @blur="handleBlur"
      ref="searchInput"
      clearable
    />
    <transition name="fade">
      <div
        v-if="showDropdown && (filteredServices.length || loading)"
        class="service-dropdown"
        @scroll.passive="onDropdownScroll"
      >
        <div v-if="loading && !filteredServices.length" class="dropdown-loader">
          <el-icon class="is-loading"><Loading /></el-icon>
          <span>Загрузка...</span>
        </div>
        <div
          v-for="(srv, idx) in filteredServices"
          :key="srv.id"
          class="dropdown-item"
          :class="{ highlighted: idx === highlighted }"
          @mousedown.prevent="selectService(srv)"
        >
          <span
            class="dropdown-name"
            :title="srv.name"
          >{{ srv.name }}</span>
          <span class="dropdown-duration">{{ srv.duration }} мин</span>
          <span class="dropdown-price">{{ srv.price }} Р</span>
        </div>
        <div v-if="loadingMore" class="dropdown-loader dropdown-loader-bottom">
          <el-icon class="is-loading"><Loading /></el-icon>
          <span>Загрузка...</span>
        </div>
      </div>
    </transition>
    <el-table
      v-if="showSelectedTable && selected.length"
      class="selected-services-table"
      :data="selected"
      size="small"
      style="margin-top: 10px;"
      show-overflow-tooltip
    >
      <el-table-column prop="name" label="Услуга"/>
      <el-table-column prop="duration" label="Минуты" width="78"/>
      <el-table-column prop="price" label="Сумма" width="90"/>
      <el-table-column width="44" class-name="service-actions-cell">
        <template #default="{ row }">
          <button class="remove-service-btn" type="button" @click="remove(row)">×</button>
        </template>
      </el-table-column>
    </el-table>
    <div v-if="showSelectedTable && selected.length" class="services-total">
      <span>Общая сумма:</span>
      <b>{{ totalPrice }} Р</b>
    </div>
  </div>
</template>

<script>
import { CloseBold, Loading } from '@element-plus/icons-vue'
import { apiClient } from '@/api'

export default {
  name: 'ServiceMultiSelect',
  props: {
    services: {
      type: Array,
      required: true
    },
    remote: {
      type: Boolean,
      default: false
    },
    cityId: {
      type: [Number, String],
      default: null
    },
    pageSize: {
      type: Number,
      default: 30
    },
    modelValue: {
      type: Array,
      default: () => []
    },
    showSelectedTable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      search: '',
      showDropdown: false,
      highlighted: -1,
      selected: [...this.modelValue],
      remoteServices: [],
      loading: false,
      loadingMore: false,
      hasMore: true,
      nextPage: 1,
      total: 0,
      searchDebounceTimer: null
    }
  },
  beforeUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = null
    }
  },
  watch: {
    modelValue(val) {
      this.selected = [...val]
    },
    cityId() {
      if (!this.remote) return
      this.loadServices({ reset: true })
    }
  },
  computed: {
    totalPrice() {
      return this.selected.reduce((sum, s) => sum + Number(s.price || 0), 0);
    },
    CloseBold() {
      return CloseBold
    },
    Loading() {
      return Loading
    },
    sourceServices() {
      return this.remote ? this.remoteServices : this.services
    },
    filteredServices() {
      return this.sourceServices.filter(s =>
        !this.selected.find(sel => sel.id === s.id) &&
        (!this.search || s.name.toLowerCase().includes(this.search.toLowerCase()))
      )
    }
  },
  methods: {
    handleFocus() {
      this.showDropdown = true
      if (this.remote && !this.remoteServices.length) {
        this.loadServices({ reset: true })
      }
    },
    handleSearchInput() {
      this.showDropdown = true
      if (!this.remote) return
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.loadServices({ reset: true })
      }, 300)
    },
    async loadServices({ reset = false } = {}) {
      if (!this.remote) return
      if (!this.cityId) {
        this.remoteServices = []
        this.total = 0
        this.hasMore = false
        return
      }
      if ((this.loading || this.loadingMore) && !reset) return
      if (!this.hasMore && !reset) return

      if (reset) {
        this.nextPage = 1
        this.hasMore = true
        this.remoteServices = []
        this.total = 0
      }

      if (reset) this.loading = true
      else this.loadingMore = true

      try {
        const res = await apiClient('services.get', {
          city_id: Number(this.cityId),
          search: this.search ? this.search.trim() : '',
          page: this.nextPage,
          pageSize: this.pageSize
        })
        const list = Array.isArray(res?.list) ? res.list : []
        const total = Number(res?.total || 0)
        this.total = total

        if (reset) {
          this.remoteServices = list
        } else {
          const existing = new Set(this.remoteServices.map(s => s.id))
          this.remoteServices = this.remoteServices.concat(list.filter(s => !existing.has(s.id)))
        }

        if (list.length > 0) {
          this.nextPage += 1
        }
        this.hasMore = this.remoteServices.length < this.total && list.length > 0
      } catch (e) {
        this.hasMore = false
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    onDropdownScroll(e) {
      if (!this.remote || this.loading || this.loadingMore || !this.hasMore) return
      const target = e.target
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 20
      if (nearBottom) {
        this.loadServices()
      }
    },
    selectService(srv) {
      if (!this.selected.find(s => s.id === srv.id)) {
        this.selected.push(srv)
        this.$emit('update:modelValue', this.selected)
        this.search = ''
        this.highlighted = -1
        this.$nextTick(() => {
          this.showDropdown = true
          this.$refs.searchInput.focus()
        })
      }
    },
    closeDropdown() {
      this.showDropdown = false
      this.highlighted = -1
      // Для Element Plus, доступ к нативному инпуту
      const inp = this.$refs.searchInput?.input || this.$refs.searchInput?.$el?.querySelector('input')
      if (inp && typeof inp.blur === 'function') inp.blur()
    },
    remove(srv) {
      this.selected = this.selected.filter(s => s.id !== srv.id)
      this.$emit('update:modelValue', this.selected)
    },
    handleBlur() {
      setTimeout(() => {
        this.showDropdown = false
        this.highlighted = -1
      }, 120)
    },
    highlightNext() {
      if (!this.showDropdown || !this.filteredServices.length) return
      this.highlighted = (this.highlighted + 1) % this.filteredServices.length
      this.scrollToHighlighted()
    },
    highlightPrev() {
      if (!this.showDropdown || !this.filteredServices.length) return
      this.highlighted =
        (this.highlighted - 1 + this.filteredServices.length) %
        this.filteredServices.length
      this.scrollToHighlighted()
    },
    enterSelect() {
      if (
        this.showDropdown &&
        this.highlighted !== -1 &&
        this.filteredServices[this.highlighted]
      ) {
        this.selectService(this.filteredServices[this.highlighted])
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const list = this.$el.querySelector('.service-dropdown')
        if (list) {
          const el = list.children[this.highlighted]
          if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
        }
      })
    }
  }
}
</script>

<style scoped>
.service-multiselect {
  position: relative;
  width: 100%;
}
.service-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #0c224b !important;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgb(0 0 0 / 30%);
  border: 1px solid #1b3f7a;
  max-height: 320px;
  overflow-y: auto;
  z-index: 9000;
  min-width: 250px;
  padding: 0;
}
.dropdown-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  color: #dce8ff;
  font-size: 13px;
}
.dropdown-loader-bottom {
  border-top: 1px solid rgb(63 111 184 / 45%);
}
.dropdown-item {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 0 16px 0 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.16s;
  border-radius: 6px;
  position: relative;
}
.dropdown-item:hover,
.dropdown-item.highlighted {
  background: #0d2757 !important;
}
.dropdown-name {
  flex: 1 1 auto;
  text-align: left;
  font-weight: 500;
  color: #dce8ff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
  display: inline-block;
  vertical-align: middle;
}
.dropdown-duration {
  flex: 0 0 80px;
  text-align: right;
  color: #9fc2ff;
  font-size: 15px;
  margin-left: 16px;
  white-space: nowrap;
}
.dropdown-price {
  flex: 0 0 80px;
  text-align: right;
  color: #57c7ff;
  font-size: 15px;
  margin-left: 8px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.services-total {
  margin-top: 8px;
  font-size: 16px;
  text-align: right;
  color: #e6efff;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.services-total b {
  font-size: 16px;
  color: #00c3fe;
  margin-left: 0;
}

.service-multiselect :deep(.selected-services-table) {
  --el-bg-color: transparent;
  --el-fill-color-blank: transparent;
  --el-table-bg-color: transparent;
  --el-table-tr-bg-color: transparent;
  --el-table-header-bg-color: transparent;
  --el-table-border-color: #031230;
  --el-table-row-hover-bg-color: rgba(24, 53, 104, 0.55);
  border-radius: 5px;
  overflow: hidden;
  background: #0c224b !important;
  border: none;
}

.service-multiselect :deep(.selected-services-table .el-table__inner-wrapper)::before,
.service-multiselect :deep(.selected-services-table .el-table__border-left-patch) {
  display: none !important;
}

.service-multiselect :deep(.selected-services-table .el-table__header-wrapper),
.service-multiselect :deep(.selected-services-table .el-table__body-wrapper),
.service-multiselect :deep(.selected-services-table .el-scrollbar__view),
.service-multiselect :deep(.selected-services-table .el-table__inner-wrapper),
.service-multiselect :deep(.selected-services-table .el-table__body),
.service-multiselect :deep(.selected-services-table .el-table__header),
.service-multiselect :deep(.selected-services-table .el-table__row) {
  background: transparent !important;
}

.service-multiselect :deep(.selected-services-table thead th.el-table__cell),
.service-multiselect :deep(.selected-services-table tbody td.el-table__cell) {
  background: transparent !important;
  border-right: 1px solid #031230 !important;
  border-bottom: 1px solid #031230 !important;
}

.service-multiselect :deep(.selected-services-table thead th.el-table__cell) {
  color: #e6efff !important;
  font-weight: 700;
  font-size: 13px;
}

.service-multiselect :deep(.selected-services-table tbody td.el-table__cell) {
  color: #00c3fe !important;
  font-size: 13px;
}

.service-multiselect :deep(.selected-services-table .el-table__cell) {
  padding: 5px 0;
}

.service-multiselect :deep(.selected-services-table tr th.el-table__cell:last-child),
.service-multiselect :deep(.selected-services-table tr td.el-table__cell:last-child) {
  border-right: none !important;
}

.service-multiselect :deep(.selected-services-table tbody tr:last-child td.el-table__cell) {
  border-bottom: none !important;
}

.service-multiselect :deep(.selected-services-table .cell) {
  white-space: nowrap;
}

.service-multiselect :deep(.selected-services-table .remove-service-btn.el-button) {
  border: none !important;
  background: #0c224b !important;
  color: #00c3fe !important;
}

.service-multiselect :deep(.selected-services-table .remove-service-btn.el-button:hover) {
  filter: brightness(1.08);
}
</style>
